import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onRegisterClick, pendingPlayerPurchase = null }) => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors
  } = useForm();

  const onSubmit = async (data) => {
    clearErrors();
    
    const result = await login(data.email, data.password);
    
    if (!result.success) {
      setError('root', {
        type: 'manual',
        message: result.error
      });
    } else {
      onClose(); // Close modal on successful login
      
      // If there's a pending player purchase, navigate to betting page with player data
      if (pendingPlayerPurchase) {
        navigate('/betting', {
          state: {
            autoSelectPlayer: pendingPlayerPurchase
          }
        });
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleOpenRegister = () => {
    onClose();          // Close login modal
    onRegisterClick();  // Open register modal
  };


  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="login-modal-close" onClick={onClose}>
          ✕
        </button>

        <div className="login-modal-content">
          {/* Left Side - Login Background */}
          <div className="login-modal-left">
            <div className="login-modal-bg">
              <img 
                src="/golfer-silhouette-men.png" 
                alt="Login Background" 
                className="login-modal-bg-image"
              />
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="login-modal-right">
            <div className="login-modal-form-container">
              {/* Logo */}
              <div className="login-modal-logo">
                <img src="/logo.png" alt="PROSKINS Logo" className="login-modal-logo-img" />
              </div>

              {/* Login Title */}
              <h1 className="login-modal-title">LOGIN</h1>

              {/* Error Message */}
              {errors.root && (
                <div className="login-modal-error">
                  <span>❌</span>
                  <div>
                    <strong>Login failed</strong>
                    <p>{errors.root.message}</p>
                  </div>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="login-modal-form">
                <div className="login-modal-form-group">
                  <label className="login-modal-label">User name</label>
                  <input
                    type="email"
                    className={`login-modal-input ${errors.email ? 'login-modal-input-error' : ''}`}
                    placeholder="Enter your email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                  />
                  {errors.email && (
                    <div className="login-modal-field-error">{errors.email.message}</div>
                  )}
                </div>

                <div className="login-modal-form-group">
                  <label className="login-modal-label">Password</label>
                  <div className="login-modal-password-container">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`login-modal-input ${errors.password ? 'login-modal-input-error' : ''}`}
                      placeholder="Enter your password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="login-modal-password-toggle"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '👁️‍🗨️' : '👁️'}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="login-modal-field-error">{errors.password.message}</div>
                  )}
                </div>

                <div className="login-modal-forgot">
                  <a href="/forgot-password" className="login-modal-forgot-link">
                    Forget Password?
                  </a>
                </div>

                <button
                  type="submit"
                  className="login-modal-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="login-modal-loading"></div>
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>

              {/* Register Link */}
              <div className="login-modal-register">
                <span className="login-modal-register-text">Don't have an account?</span>
                <button className="login-modal-register-link" onClick={handleOpenRegister}>REGISTER NOW</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
